/**
 * Helpers para manejo de audio cross-browser
 */

import { getAudioContext } from './polyfills';

export interface AudioRecorderOptions {
  mimeType?: string;
  audioBitsPerSecond?: number;
}

export class AudioRecorderHelper {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  async startRecording(): Promise<void> {
    try {
      // Solicitar permisos de micrófono con el procesamiento de audio del
      // navegador ACTIVADO. Antes se pedía `{ audio: true }` a secas, que en
      // varios navegadores no aplica DSP y deja pasar todo el ruido de ambiente
      // (salón, bus, webcams que captan el entorno) → el STT se degrada y el
      // alumno siente que "el micrófono no funciona".
      //  - noiseSuppression: filtra ruido de fondo estacionario (ventilador,
      //    murmullo del salón).
      //  - echoCancellation: evita que el TTS de Galia que sale por los
      //    parlantes vuelva a entrar por el micro.
      //  - autoGainControl: normaliza el volumen (alumno lejos/cerca del mic).
      // Son constraints "ideales" (booleans, no `{ exact }`): un navegador que
      // no las soporte simplemente las ignora, no falla getUserMedia.
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });
      
      // Determinar el tipo MIME por CAPACIDAD, no por user-agent. Todos los
      // navegadores de iOS son WebKit (Chrome/Firefox/Edge incluidos) y solo
      // graban audio/mp4; Chrome/Firefox/Android prefieren webm. Preguntar a
      // isTypeSupported cubre todos los casos sin lista de UAs. Si nada de la
      // lista es soportado, se omite mimeType y el navegador usa su default —
      // el backend detecta el contenedor real por magic bytes (stt.mjs), asi
      // que cualquier formato que salga de aqui se transcribe bien.
      const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg'];
      const mimeType = candidates.find((c) => MediaRecorder.isTypeSupported(c));

      const options: AudioRecorderOptions = {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 128000,
      };

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start();
    } catch (error) {
      console.error('Error al iniciar grabación:', error);
      throw error;
    }
  }

  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No hay grabación activa'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
        this.cleanup();
        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (error) => {
        this.cleanup();
        reject(error);
      };

      this.mediaRecorder.stop();
    });
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  async convertToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export function initializeAudioContext(): AudioContext | null {
  const audioContext = getAudioContext();

  if (audioContext && audioContext.state === 'suspended') {
    // En algunos navegadores, el AudioContext se suspende hasta que hay interacción del usuario
    audioContext.resume().catch(err => {
      console.warn('No se pudo reanudar AudioContext:', err);
    });
  }

  return audioContext;
}

/**
 * Desbloquea la reproducción de audio en iOS Safari (y Android restrictivo).
 * DEBE llamarse SINCRÓNICAMENTE dentro del handler de un gesto de usuario
 * (click/tap), ANTES de cualquier `await`. De lo contrario iOS pierde el
 * contexto de gesto y sigue bloqueando.
 *
 * Qué hace:
 *  1. Reanuda el AudioContext (Web Audio API).
 *  2. Reproduce un WAV silencioso en un HTMLAudioElement para establecer la
 *     "autorización de reproducción" del dominio en iOS. Tras esto, los
 *     `new Audio(url).play()` posteriores (p. ej. chunks TTS del WS) pueden
 *     reproducir automáticamente sin requerir otro gesto.
 *
 * Llamar UNA vez por sesión de página es suficiente.
 */
let audioUnlocked = false;
export function unlockAudioPlayback(): void {
  if (audioUnlocked) return;

  // 1) Web Audio API
  initializeAudioContext();

  // 2) HTMLAudioElement — reproducir un WAV silencioso de ~1ms.
  try {
    const silentWav =
      'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
    const primer = new Audio(silentWav);
    primer.volume = 0;
    const playResult = primer.play();
    if (playResult && typeof playResult.then === 'function') {
      playResult.then(() => { audioUnlocked = true; }).catch(() => { /* ignorar */ });
    } else {
      audioUnlocked = true;
    }
  } catch {
    /* ignorar — si falla, se reintenta en el próximo gesto */
  }
}


